import { FC, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import {
  BRANDING_KINDS,
  BRANDING_LABELS,
  BrandingKind,
  deleteCommunityBranding,
  getCommunityBranding,
  uploadCommunityBranding,
  VIDEO_CAPABLE_KINDS,
} from "@/api/communityAdmin";

import { BrandingMedia } from "./BrandingMedia";

// View + upload/replace an org's branding assets. Self-contained and
// orgId-scoped so it can be mounted anywhere (the org-config page tab and the
// onboarding wizard both render it). Only the asset URL is tracked — whether it
// renders as an image or a video is decided by BrandingMedia inspecting the file.
export const OrgBrandingPanel: FC<{ orgId: string }> = ({ orgId }) => {
  const [urls, setUrls] = useState<Partial<Record<BrandingKind, string>>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<
    Partial<Record<BrandingKind, "uploading" | "removing" | "error">>
  >({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getCommunityBranding(orgId)
      .then((b) => {
        if (!active) return;
        const next: Partial<Record<BrandingKind, string>> = {};
        for (const kind of BRANDING_KINDS) {
          const url = b[kind]?.url;
          if (url) next[kind] = url;
        }
        setUrls(next);
        setLoadError(null);
      })
      .catch((e) => {
        if (active)
          setLoadError(
            e instanceof Error ? e.message : "Failed to load branding",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  const handleFile = async (kind: BrandingKind, file: File) => {
    setStatus((s) => ({ ...s, [kind]: "uploading" }));
    setError(null);
    try {
      const res = await uploadCommunityBranding(orgId, kind, file);
      setUrls((u) => ({ ...u, [kind]: res.url }));
      setStatus((s) => {
        const next = { ...s };
        delete next[kind];
        return next;
      });
    } catch (e) {
      setStatus((s) => ({ ...s, [kind]: "error" }));
      setError(e instanceof Error ? e.message : `Failed to upload ${kind}`);
    }
  };

  const handleRemove = async (kind: BrandingKind) => {
    if (
      !window.confirm(
        `Remove the ${BRANDING_LABELS[kind]} asset? The app falls back to its bundled default.`,
      )
    )
      return;
    setStatus((s) => ({ ...s, [kind]: "removing" }));
    setError(null);
    try {
      await deleteCommunityBranding(orgId, kind);
      setUrls((u) => {
        const next = { ...u };
        delete next[kind];
        return next;
      });
      setStatus((s) => {
        const next = { ...s };
        delete next[kind];
        return next;
      });
    } catch (e) {
      setStatus((s) => ({ ...s, [kind]: "error" }));
      setError(e instanceof Error ? e.message : `Failed to remove ${kind}`);
    }
  };

  if (loading) return <Typography>Loading…</Typography>;

  return (
    <Stack spacing={1}>
      {loadError && <Alert severity="warning">{loadError}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Current branding for this organization. Uploading replaces the asset in
        place. Hero and login background may be a video.
      </Typography>
      {BRANDING_KINDS.map((kind) => {
        const url = urls[kind];
        const uploading = status[kind] === "uploading";
        const removing = status[kind] === "removing";
        const busy = uploading || removing;
        return (
          <Box
            key={kind}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" sx={{ width: 200, flexShrink: 0 }}>
              {BRANDING_LABELS[kind]}
            </Typography>
            <Box
              sx={{
                width: 120,
                height: 72,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {url ? (
                <BrandingMedia
                  url={url}
                  alt={kind}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  None
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              component="label"
              disabled={busy}
              sx={{ flexShrink: 0 }}
            >
              {url ? "Replace" : "Upload"}
              <input
                type="file"
                hidden
                accept={
                  VIDEO_CAPABLE_KINDS.has(kind)
                    ? "image/*,video/mp4,video/webm"
                    : "image/*"
                }
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(kind, file);
                  e.target.value = "";
                }}
              />
            </Button>
            {url && (
              <Button
                color="error"
                disabled={busy}
                onClick={() => handleRemove(kind)}
                sx={{ flexShrink: 0 }}
              >
                Remove
              </Button>
            )}
            {busy && <CircularProgress size={18} />}
            {status[kind] === "error" && (
              <Typography color="error.main" variant="body2">
                failed
              </Typography>
            )}
            {url && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, wordBreak: "break-all" }}
              >
                {url}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default OrgBrandingPanel;
